import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import CheckBoxAttribute from '../CheckBoxAttribute';
import ConfirmationModal from '../../ConfirmationModal';
import { HOST_SET_ROUTE } from '../../../utils/constants';
import { ERROR_TEXT_NO_THANK_YOU_URL } from '../../../langs/en/errors';
import ThankYouRouteRow from './components/ThankYouRouteRow';
import { iconThankYouRoute } from '../../../utils/svgConstants';

const EMPTY_FORM_DATA = {};
const EMPTY_OPTIONS = {};

const ThankYouRouteEditor = ({ keyPath, isThankYouField, values, toggleThankYouRoute, options = EMPTY_OPTIONS, setThankYouRoute, refreshThankYouRoute, fieldValidityChanged, invalidFormData = EMPTY_FORM_DATA }) => {
  const [error, setError] = useState({});
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    setError({});
    if (invalidFormData && invalidFormData.fields && invalidFormData.fields[values.id]) {
      setError(invalidFormData.fields[values.id]);
    }
  }, [invalidFormData, values]);

  const validOptions = values.editors.dropdownConfig.options;
  const hashedVisibility = Object.keys(validOptions).map((fieldName) => validOptions[fieldName].shouldDisplay.toString()).join('');

  useEffect(() => {
    if (isThankYouField) {
      refreshThankYouRoute(values.id);
    }
  }, [hashedVisibility]);

  const deleteText = `This action will delete all thank you routing assignments for this question.
  \rDo you want to proceed?`;

  const handleCheckboxChange = (_, val) => {
    if (!val) {
      setShowModal(true);
    } else {
      toggleThankYouRoute(values.id);
    }
  };

  const onHostRouteChange = (value, label) => {
    if (value) {
      fieldValidityChanged([...keyPath, label], false, ERROR_TEXT_NO_THANK_YOU_URL);
      setThankYouRoute(values.id, label, '');
    } else {
      fieldValidityChanged([...keyPath, label], true);
      setThankYouRoute(values.id, label, HOST_SET_ROUTE);
    }
  };

  const optionsSorted = [];

  (values.editors.dropdownConfig.optionsOrder || []).forEach((option) => {
    const { value: fieldValue } = values.editors.dropdownConfig.options[option];
    const optionsKeys = Object.keys(options);
    const index = optionsKeys.indexOf(fieldValue);
    if (index >= 0) {
      optionsSorted.push(optionsKeys[index]);
    }
  });

  return (
    <div>
      <div className="row px-5 pb-3">
        <div className="col mt-2">
          <div className="row">
            <div className="d-inline-flex">
              <CheckBoxAttribute
                className="mt-1"
                onCheckBoxChange={handleCheckboxChange}
                keyPath={[...keyPath, 'thankYouCheckbox']}
                value={isThankYouField}
                labelClassName="font-weight-bold"
              >Make this a thank you page routing question
              </CheckBoxAttribute>
              <span className="ml-8">
                <svg className="fill-branching-gray" xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 512 512">
                  <g>
                    <path d={iconThankYouRoute} />
                  </g>
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>
      {isThankYouField && (
        <div id="conditional-content">
          <div className="font-italic small py-2">
            Note: Thank you URL can be either a <span className="font-weight-bold">relative</span> (/thank-you.html - note must start with /) or <span className="font-weight-bold">absolute</span> URL (https://2u.com/thank-you.html)
          </div>
          <div className="row">
            <div className="col p-0 mb-2">
              <div className="branch-follow-editor bg-branching-green">
                <div className="row">
                  <div className="col-12 mb-3">
                    {optionsSorted.map((label) => (
                      <ThankYouRouteRow
                        key={label}
                        label={label}
                        onHostRouteChange={onHostRouteChange}
                        setThankYouRoute={setThankYouRoute}
                        error={error}
                        fieldValidityChanged={fieldValidityChanged}
                        keyPath={keyPath}
                        values={values}
                        options={options}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmationModal
        title="Remove Thank You Routing"
        onCancel={() => setShowModal(false)}
        visible={showModal}
        confirmText="Remove Thank You Routing"
        cancelText="Cancel"
        onConfirm={() => {
          setShowModal(false);
          toggleThankYouRoute(values.id);
        }}
        content={deleteText}
      />
    </div>
  );
};

ThankYouRouteEditor.propTypes = {
  toggleThankYouRoute: PropTypes.func.isRequired,
  values: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    editors: PropTypes.object.isRequired,
  }).isRequired,
  keyPath: PropTypes.array.isRequired,
  isThankYouField: PropTypes.bool.isRequired,
  options: PropTypes.object,
  setThankYouRoute: PropTypes.func.isRequired,
  refreshThankYouRoute: PropTypes.func.isRequired,
  fieldValidityChanged: PropTypes.func.isRequired,
  invalidFormData: PropTypes.object,
};

export default ThankYouRouteEditor;
